import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Auth } from 'aws-amplify';
import { environment } from '../../../environments/environment';

// Helper function to get user role
export async function getUserRole(): Promise<'admin' | 'owners' | 'user'> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const groups = user.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
    
    if (groups.includes('admin') && groups.includes('owners')) {
      return 'admin'; // Admin tiene prioridad si tiene ambos roles
    } else if (groups.includes('admin')) {
      return 'admin';
    } else if (groups.includes('owners')) {
      return 'owners';
    } else {
      return 'user';
    }
  } catch (error) {
    return 'user'; // Default to user if there's an error
  }
}

// Helper function to check if user has specific role
export async function hasRole(role: string): Promise<boolean> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const groups = user.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
    return groups.includes(role);
  } catch (error) {
    return false;
  }
}

export const AuthGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Bypass guard in development if flag is set
  if (environment.disableAuthGuard) {
    console.log('AuthGuard bypassed - development mode');
    return true;
  }

  const router = inject(Router);
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch (error) {
    router.navigate(['/auth']);
    return false;
  }
};

export const AdminGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Bypass guard in development if flag is set
  if (environment.disableAuthGuard) {
    console.log('AdminGuard bypassed - development mode');
    return true;
  }

  const router = inject(Router);
  try {
    const user = await Auth.currentAuthenticatedUser();
    
    // Get user groups from Cognito
    const groups = user.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
    
    // Check if user has admin role
    const isAdmin = groups.includes('admin');
    
    if (!isAdmin) {
      router.navigate(['/unauthorized']);
      return false;
    }
    
    return true;
  } catch (error) {
    router.navigate(['/auth']);
    return false;
  }
};

export const OwnerGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Bypass guard in development if flag is set
  if (environment.disableAuthGuard) {
    console.log('OwnerGuard bypassed - development mode');
    return true;
  }

  const router = inject(Router);
  try {
    const user = await Auth.currentAuthenticatedUser();
    
    // Get user groups from Cognito
    const groups = user.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
    
    // Check if user has owner role
    const isOwner = groups.includes('owners');
    
    if (!isOwner) {
      router.navigate(['/unauthorized']);
      return false;
    }
    
    return true;
  } catch (error) {
    router.navigate(['/auth']);
    return false;
  }
};

export const AdminOrOwnerGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Bypass guard in development if flag is set
  if (environment.disableAuthGuard) {
    console.log('AdminOrOwnerGuard bypassed - development mode');
    return true;
  }

  const router = inject(Router);
  try {
    const user = await Auth.currentAuthenticatedUser();
    
    // Get user groups from Cognito
    const groups = user.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
    
    // Check if user has admin or owner role
    const isAdmin = groups.includes('admin');
    const isOwner = groups.includes('owners');
    
    if (!isAdmin && !isOwner) {
      router.navigate(['/unauthorized']);
      return false;
    }
    
    return true;
  } catch (error) {
    router.navigate(['/auth']);
    return false;
  }
};

export const UserGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Bypass guard in development if flag is set
  if (environment.disableAuthGuard) {
    console.log('UserGuard bypassed - development mode');
    return true;
  }

  const router = inject(Router);
  try {
    const user = await Auth.currentAuthenticatedUser();
    
    // Get user groups from Cognito
    const groups = user.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
    
    const isAdmin = groups.includes('admin');
    const isOwner = groups.includes('owners');
    const isUser = groups.length === 0 || (!isAdmin && !isOwner);
    
    // Solo permitir acceso a usuarios regulares (sin roles especiales)
    if (!isUser) {
      router.navigate(['/unauthorized']);
      return false;
    }
    
    return true;
  } catch (error) {
    router.navigate(['/auth']);
    return false;
  }
};

export const RoleBasedRedirectGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Bypass guard in development if flag is set
  if (environment.disableAuthGuard) {
    console.log('RoleBasedRedirectGuard bypassed - development mode');
    return true;
  }

  const router = inject(Router);
  try {
    const user = await Auth.currentAuthenticatedUser();
    
    // Get user groups from Cognito
    const groups = user.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
    
    const isAdmin = groups.includes('admin');
    const isOwner = groups.includes('owners');
    
    // Si es Owner sin Admin, redirigir a manual-rooms
    if (isOwner && !isAdmin) {
      router.navigate(['/home/manual-rooms']);
      return false;
    }
    
    // Si es Admin (con o sin Owner), permitir acceso al dashboard
    if (isAdmin) {
      return true;
    }
    
    // Si no tiene roles (usuario regular), permitir acceso al dashboard
    return true;
  } catch (error) {
    router.navigate(['/auth']);
    return false;
  }
};